<template>
	<v-dialog
		v-model="open"
		max-width="700"
		@click:outside="$emit('close')"
		:fullscreen="$vuetify.breakpoint.xs ? true : false"
		:transition="$vuetify.breakpoint.xs ? 'scroll-y-reverse-transition' : ''"
	>
		<v-card :tile="$vuetify.breakpoint.xs">
			<v-toolbar dark color="primary" dense flat>
				<v-toolbar-title style="color: white">{{ customerCompany.name }}</v-toolbar-title>
				<v-spacer></v-spacer>
				<v-btn icon dark @click="$emit('close')" color="white">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-sheet elevation="0" class="pa-2">
				<v-row no-gutters justify="end" v-if="authorized">
					<v-btn
						class="ml-auto mr-2"
						color="secondary"
						@click="
							$router.push({ name: 'customer_company_edit', params: { id: customerCompany.id } })
						"
						dense
					>
						<v-icon class="mr-2">mdi-pencil</v-icon>
						編集する</v-btn
					>
					<TemplateDeleteDialog :deleteMethod="deleteCustomerCompany" />
				</v-row>
				<v-sheet elevation="0" class="mt-4">
					<h4 class="text-h6 font-weight-bold color-secondary mb-2">顧客企業情報</h4>
					<table width="100%" class="show-dialog-table">
						<tbody>
							<tr>
								<th width="30%" class="font-weight-bold">顧客企業名・拠点名</th>
								<CustomerCompanyEditableCell
									:value="customerCompany.name"
									:rules="[rules.required]"
									:modelName="'customer_company'"
									:columnName="'name'"
									:authorized="authorized"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="updateCustomerCompany($event, 'name')"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">顧客企業名・拠点名（フルネーム）</th>
								<CustomerCompanyEditableCell
									:value="customerCompany.full_name"
									:modelName="'customer_company'"
									:columnName="'full_name'"
									:authorized="authorized"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="updateCustomerCompany($event, 'full_name')"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">得意先コード</th>
								<CustomerCompanyEditableCell
									:value="customerCompany.customer_code"
									:modelName="'customer_company'"
									:columnName="'customer_code'"
									:authorized="authorized"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="updateCustomerCompany($event, 'customer_code')"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">郵便番号</th>
								<CustomerCompanyEditableCell
									:value="customerCompany.zipcode"
									:modelName="'customer_company'"
									:columnName="'zipcode'"
									:authorized="authorized"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="updateCustomerCompany($event, 'zipcode')"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">都道府県</th>
								<CustomerCompanyEditableCell
									:formType="'select'"
									:items="prefectures"
									:value="customerCompany.prefecture"
									:modelName="'customer_company'"
									:columnName="'prefecture'"
									:authorized="authorized"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="updateCustomerCompany($event, 'prefecture')"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">市区町村</th>
								<CustomerCompanyEditableCell
									:value="customerCompany.city"
									:modelName="'customer_company'"
									:columnName="'city'"
									:authorized="authorized"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="updateCustomerCompany($event, 'city')"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">住所</th>
								<CustomerCompanyEditableCell
									:value="customerCompany.address"
									:formType="'textarea'"
									:modelName="'customer_company'"
									:columnName="'address'"
									:authorized="authorized"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="updateCustomerCompany($event, 'address')"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">建物名・部屋番号</th>
								<CustomerCompanyEditableCell
									:value="customerCompany.building"
									:formType="'textarea'"
									:modelName="'customer_company'"
									:columnName="'building'"
									:authorized="authorized"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="updateCustomerCompany($event, 'building')"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">代表電話番号</th>
								<CustomerCompanyEditableCell
									:value="customerCompany.phone"
									:modelName="'customer_company'"
									:columnName="'phone'"
									:authorized="authorized"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="updateCustomerCompany($event, 'phone')"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">代表FAX番号</th>
								<CustomerCompanyEditableCell
									:value="customerCompany.fax"
									:modelName="'customer_company'"
									:columnName="'fax'"
									:authorized="authorized"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="updateCustomerCompany($event, 'fax')"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">代表メールアドレス</th>
								<CustomerCompanyEditableCell
									:value="customerCompany.email"
									:modelName="'customer_company'"
									:rules="[rules.emailFormat]"
									:columnName="'email'"
									:authorized="authorized"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="updateCustomerCompany($event, 'email')"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">業種名</th>
								<CustomerCompanyEditableCell
									:formType="'select'"
									:value="customerCompany.industry ? customerCompany.industry.id : ''"
									:name="customerCompany.industry ? customerCompany.industry.name : ''"
									:modelName="'customer_company'"
									:columnName="'industry_id'"
									:authorized="authorized"
									:items="industries"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="updateCustomerCompany($event, 'industry')"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">締め日</th>
								<CustomerCompanyEditableCell
									:formType="'select'"
									:value="customerCompany.cutoff_date"
									:name="customerCompany.cutoff_date_i18n"
									:modelName="'customer_company'"
									:columnName="'cutoff_date'"
									:authorized="authorized"
									:items="cutoffDates"
									:url="`${customerCompanyUrl}${customerCompany.id}`"
									@update-column="
										updateCustomerCompany($event, ['cutoff_date_i18n', 'cutoff_date'])
									"
								/>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">登録タグ一覧</th>
								<td width="70%">
									<v-chip
										v-for="tag in customerCompany.customer_company_tags"
										small
										class="ma-0 mr-2"
										:key="tag.id"
										color="primary"
										outlined
									>
										{{ tag.name }}
									</v-chip>
								</td>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">社員一覧</th>
								<td width="70%">
									<v-row
										no-gutters
										class="top-divider"
										justify="space-between"
										align="center"
										v-for="(customer_person, i) in customerCompany.customer_people"
										:key="i"
									>
										<span>
											{{ customer_person.name }}
										</span>
										<v-menu offset-y elevation="0">
											<template v-slot:activator="{ on, attrs }">
												<v-btn text color="black" v-bind="attrs" v-on="on"> 詳細 </v-btn>
											</template>
											<v-card class="pa-2">
												<table class="show-dialog-table">
													<tbody>
														<tr>
															<th width="30%" class="font-weight-bold">名前</th>
															<td width="70%">{{ customer_person.name }}</td>
														</tr>
														<tr>
															<th width="30%" class="font-weight-bold">名前（カナ）</th>
															<td width="70%">{{ customer_person.name_kana }}</td>
														</tr>
														<tr>
															<th width="30%" class="font-weight-bold">生年月日</th>
															<td width="70%">{{ customer_person.birth_day }}</td>
														</tr>
														<tr>
															<th width="30%" class="font-weight-bold">性別</th>
															<td width="70%">{{ customer_person.gender }}</td>
														</tr>
														<tr>
															<th width="30%" class="font-weight-bold">役職</th>
															<td width="70%">{{ customer_person.position }}</td>
														</tr>
														<tr>
															<th width="30%" class="font-weight-bold">電話番号</th>
															<td width="70%">{{ customer_person.phone }}</td>
														</tr>
														<tr>
															<th width="30%" class="font-weight-bold">FAX番号</th>
															<td width="70%">{{ customer_person.fax }}</td>
														</tr>
														<tr>
															<th width="30%" class="font-weight-bold">メールアドレス</th>
															<td width="70%">{{ customer_person.email }}</td>
														</tr>
														<tr>
															<th width="30%" class="font-weight-bold">部門</th>
															<td width="70%">{{ customer_person.department }}</td>
														</tr>
													</tbody>
												</table>
											</v-card>
										</v-menu>
									</v-row>
								</td>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">登録日</th>
								<td width="70%">{{ common.formatDateTime(customerCompany.created_at) }}</td>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">最終更新日</th>
								<td width="70%">{{ common.formatDateTime(customerCompany.updated_at) }}</td>
							</tr>
						</tbody>
					</table>
					<template v-if="customerCompany.customer_company_share_info">
						<h4 class="text-h6 font-weight-bold color-secondary mt-4 mb-2">顧客企業共通情報</h4>
						<table width="100%" class="show-dialog-table">
							<tbody>
								<tr>
									<th width="30%" class="font-weight-bold">顧客企業名</th>
									<CustomerCompanyEditableCell
										:value="customerCompany.customer_company_share_info.name"
										:modelName="'share_info'"
										:columnName="'name'"
										:authorized="authorized"
										:rules="[rules.required]"
										:url="`${shareInfoUrl}${customerCompany.customer_company_share_info.id}`"
										@update-column="updateShareInfoColumn($event, 'name')"
									/>
								</tr>
								<tr>
									<th width="30%" class="font-weight-bold">創業年月日</th>
									<CustomerCompanyEditableCell
										:value="customerCompany.customer_company_share_info.establishment_date"
										:modelName="'share_info'"
										:columnName="'establishment_date'"
										:formType="'date-select'"
										:authorized="authorized"
										:url="`${shareInfoUrl}${customerCompany.customer_company_share_info.id}`"
										:date="true"
										@update-column="updateShareInfoColumn($event, 'establishment_date')"
									/>
								</tr>
								<tr>
									<th width="30%" class="font-weight-bold">資本金</th>
									<CustomerCompanyEditableCell
										:value="customerCompany.customer_company_share_info.capital"
										:modelName="'share_info'"
										:columnName="'capital'"
										:type="'number'"
										:suffix="'円'"
										:authorized="authorized"
										:hint="'半角正数のみ使用可能です。'"
										:url="`${shareInfoUrl}${customerCompany.customer_company_share_info.id}`"
										@update-column="updateShareInfoColumn($event, 'capital')"
									/>
								</tr>
								<tr>
									<th width="30%" class="font-weight-bold">従業員数</th>
									<CustomerCompanyEditableCell
										:value="customerCompany.customer_company_share_info.employees_number"
										:modelName="'share_info'"
										:columnName="'employees_number'"
										:authorized="authorized"
										:hint="'半角正数のみ使用可能です。'"
										:type="'number'"
										:suffix="'人'"
										:url="`${shareInfoUrl}${customerCompany.customer_company_share_info.id}`"
										@update-column="updateShareInfoColumn($event, 'employees_number')"
									/>
								</tr>
								<tr>
									<th width="30%" class="font-weight-bold">代表者名</th>
									<CustomerCompanyEditableCell
										:value="customerCompany.customer_company_share_info.representative"
										:modelName="'share_info'"
										:columnName="'representative'"
										:authorized="authorized"
										:url="`${shareInfoUrl}${customerCompany.customer_company_share_info.id}`"
										@update-column="updateShareInfoColumn($event, 'representative')"
									/>
								</tr>
								<tr>
									<th width="30%" class="font-weight-bold">代表者名（カナ）</th>
									<CustomerCompanyEditableCell
										:value="customerCompany.customer_company_share_info.representative_kana"
										:modelName="'share_info'"
										:columnName="'representative_kana'"
										:authorized="authorized"
										:url="`${shareInfoUrl}${customerCompany.customer_company_share_info.id}`"
										@update-column="updateShareInfoColumn($event, 'representative_kana')"
									/>
								</tr>
								<tr>
									<th width="30%" class="font-weight-bold">Webサイト</th>
									<CustomerCompanyEditableCell
										:value="customerCompany.customer_company_share_info.website"
										:modelName="'share_info'"
										:columnName="'website'"
										:authorized="authorized"
										:url="`${shareInfoUrl}${customerCompany.customer_company_share_info.id}`"
										@update-column="updateShareInfoColumn($event, 'website')"
									/>
								</tr>
								<tr>
									<th width="30%" class="font-weight-bold">登録日</th>
									<td width="70%">
										{{
											common.formatDateTime(customerCompany.customer_company_share_info.created_at)
										}}
									</td>
								</tr>
								<tr>
									<th width="30%" class="font-weight-bold">最終更新日</th>
									<td width="70%">
										{{
											common.formatDateTime(customerCompany.customer_company_share_info.updated_at)
										}}
									</td>
								</tr>
							</tbody>
						</table>
					</template>
					<template v-if="customerCompany.memos.length">
						<h4 class="text-h6 font-weight-bold color-secondary mt-4 mb-2">メモ</h4>
						<table width="100%" class="show-dialog-table">
							<tbody class="border-top" v-for="memo in customerCompany.memos" :key="memo.id">
								<tr>
									<th width="30%" class="font-weight-bold">内容</th>
									<td width="70%"><p v-text="memo.content" /></td>
								</tr>
								<tr>
									<th width="30%" class="font-weight-bold">メモ作成者</th>
									<td width="70%">{{ memo.user.full_name }}</td>
								</tr>
								<tr>
									<th width="30%" class="font-weight-bold">添付ファイル</th>
									<td width="70%" max-width="70%">
										<template v-if="memo.files_with_url.length">
											<v-btn
												tag="button"
												max-width="100%"
												outlined
												small
												class="ma-1 btn-no-transform dialog-file-btn"
												color="primary"
												v-for="file in memo.files_with_url"
												:key="file.id"
												:href="file.url"
												target="_blank"
											>
												<v-icon class="mr-2">mdi-download</v-icon>
												<p v-text="file.name"></p>
											</v-btn>
										</template>
										<template v-else>なし</template>
									</td>
								</tr>
								<tr></tr>
							</tbody>
						</table>
					</template>
				</v-sheet>
			</v-sheet>
			<v-card-actions>
				<v-row no-gutters>
					<v-col cols="8" />
					<v-col cols="4">
						<v-row no-gutters justify="end">
							<v-btn color="grey darken-1" text @click="$emit('close')"> 閉じる </v-btn>
						</v-row>
					</v-col>
				</v-row>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import axios from "axios"
import authenticate from "../../../helper/authenticate"
import errorHandling from "../../../helper/errorHandling"
import common from "../../../helper/common"
import rules from "../../../helper/rules"
import TemplateDeleteDialog from "../../shared/TemplateDeleteDialog.vue"
import CustomerCompanyEditableCell from "../../shared/EditableTd.vue"

export default {
	props: {
		customer_company: {
			type: Object,
			required: true,
			default: () => ({}),
		},
		authorized: {
			type: Boolean,
			required: false,
			default: false,
		},
		industries: {
			type: Array,
			required: true,
		},
		cutoffDates: {
			type: Array,
			required: true,
		},
	},
	components: {
		TemplateDeleteDialog,
		CustomerCompanyEditableCell,
	},
	data() {
		return {
			open: true,
			customerCompanyUrl: "/erp/v1/customer_companies/update_cells/",
			shareInfoUrl: "/erp/v1/customer_company_share_infos/update_cells/",
			customerPersonUrl: "/erp/v1/customer_people/",
			prefectures: this.$store.state.prefectures,
			common,
			rules,
		}
	},
	methods: {
		updateCustomerCompany(customer_company, columnName) {
			if (Array.isArray(columnName)) {
				columnName.forEach((name) => {
					this.customerCompany[name] = customer_company[name]
				})
			} else {
				this.customerCompany[columnName] = customer_company[columnName]
			}
			this.customerCompany.updated_at = customer_company.updated_at
			if (columnName == "name" || "customer_code" || "industry") {
				this.$emit("update-column", { customer_company, columnName })
			}
		},
		updateShareInfoColumn(share_info, columnName) {
			this.customerCompany.customer_company_share_info[columnName] = share_info[columnName]
			this.customerCompany.customer_company_share_info.updated_at = share_info.updated_at
		},
		deleteCustomerCompany() {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/customer_companies/${this.customer_company.id}`
			axios
				.delete(url, { headers })
				.then((res) => {
					this.$emit("reload-customer-companies")
					this.$emit("close")
					this.$store.commit("setFlash", {
						text: "削除しました",
						type: "notice",
					})
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
	},
	computed: {
		customerCompany: {
			get() {
				return this.customer_company
			},
			set(newVal) {
				return this.$emit("update:customer_company", newVal)
			},
		},
	},
}
</script>

<style lang="scss" scoped>
.v-expansion-panel-content__wrap {
	padding: 8px;
}
table {
	.border-top {
		border-top: 4px white solid;
	}
}
</style>